import { Box, CircularProgress, Tooltip } from "@mui/material";
import { DescriptionOutlined } from "@mui/icons-material";
import type { RefObject } from "react";
import type { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { SimpleToolbar } from "@/components/layout/Toolbar";
import { MenuDropDown } from "@/components/common/MenuDropDown";
import { CustomButton } from "@/components/common/customButton";
import { CustomIconButton } from "@/components/common/customIconButton";
import { NotificationBellButton } from "@/components/notifications/NotificationBell";
import { DataCompletenessButton } from "@/components/dataCompleteness/DataCompletenessButton";
import pencilIcon from "@/assets/svg/edit.svg";
import downloadIcon from "@/assets/svg/downloads.svg";
import { PipelineViewTabs } from "./components/PipelineViewTabs";
import {
  PipelineFilterBar,
  type PipelineFilterBarProps,
} from "./components/PipelineFilterBar";
import type { PipelineViewId } from "./pipelineViews";
import { isStatusBandRow } from "./pipelineMeeting";
import {
  downloadDealVolumeReport,
  downloadPipelineExcelReport,
} from "./pipelineDownloads";
import type { TenantFeatureKey } from "@/types/tenantFeatures";
import type { ApiResponse } from "@/services/genesisApi/httpsMethods";

type PipelineToolbarProps = PipelineFilterBarProps & {
  isLive: boolean;
  isSaving: boolean;
  view: PipelineViewId;
  setView: (view: PipelineViewId) => void;
  customColumns: string[];
  setCustomColumns: (fields: string[]) => void;
  allColumns: ColDef[];
  canEdit: boolean;
  editing: boolean;
  setEditing: (value: boolean) => void;
  gridRef: RefObject<AgGridReact | null>;
  isFeatureEnabled: (feature: TenantFeatureKey) => boolean;
  showErrorFromResponse: (response?: ApiResponse) => void;
};

/**
 * The app's standard toolbar (view tabs left, actions right, as on Summary),
 * with search, filters and deal scope on the row underneath.
 */
export function PipelineToolbar(props: PipelineToolbarProps) {
  const {
    isLive,
    isSaving,
    view,
    setView,
    customColumns,
    setCustomColumns,
    allColumns,
    canEdit,
    editing,
    setEditing,
    gridRef,
    isFeatureEnabled,
    showErrorFromResponse,
    ...filterProps
  } = props;

  const pipelineMenuItems: Parameters<typeof MenuDropDown>[0]["menuItems"] = [
    {
      label: "Export to CSV",
      onClick: () => {
        gridRef.current?.api.exportDataAsCsv({
          fileName: "pipeline",
          // Meeting-view status bands are headings, not deals.
          shouldRowBeSkipped: ({ node }) => isStatusBandRow(node.data),
        });
      },
      icon: (
        <Box
          component="img"
          src={downloadIcon}
          alt="Download"
          sx={{ width: 16, height: 16, display: "block", marginLeft: "2px" }}
        />
      ),
    },
  ];

  if (isFeatureEnabled("deal_volume_pdf_report")) {
    pipelineMenuItems.push({
      label: "Generate PDF Pipeline",
      onClick: () => void downloadDealVolumeReport(showErrorFromResponse),
      icon: <DescriptionOutlined fontSize="small" />,
    });
  }

  if (isFeatureEnabled("pipeline_excel_report")) {
    pipelineMenuItems.push({
      label: "Generate Excel Pipeline",
      onClick: () => void downloadPipelineExcelReport(showErrorFromResponse),
      icon: <DescriptionOutlined fontSize="small" />,
    });
  }

  return (
    <>
      {/* Same wrapper as the Summary page's tab bar. */}
      <Box
        sx={{
          padding: "0px",
          position: "relative",
          zIndex: 10,
          backgroundColor: "background.paper",
          boxShadow: "0px 1px 5px 0px rgba(44, 62, 80, 0.12)",
          "& .toolbar": {
            top: 0,
            alignItems: "center",
            flexWrap: "nowrap",
            minWidth: 0,
            maxWidth: "100%",
            borderBottom: "none",
          },
          "& .toolbar-left": {
            flex: "1 1 10%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
            padding: "4px 8px",
            flexWrap: "nowrap",
            alignItems: "center",
          },
          "& .toolbar-right": {
            flex: "0 0 auto",
            width: "auto",
            maxWidth: "none",
            marginLeft: "0 !important",
            flexShrink: 0,
            justifyContent: "flex-end",
            paddingRight: "15px",
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        <SimpleToolbar customToolbarPadding="0px 0px">
          <Box sx={{ minWidth: 0, display: "flex", alignSelf: "stretch" }}>
            <PipelineViewTabs
              view={view}
              setView={setView}
              customColumns={customColumns}
              setCustomColumns={setCustomColumns}
              columns={allColumns}
            />
          </Box>
          <Box
            data-position="right"
            sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
          >
            <LiveStatus isLive={isLive} isSaving={isSaving} />
            {canEdit && editing && (
              <Tooltip title="Every change saves as you make it. Click to stop editing">
                <span>
                  <CustomButton
                    handleButtonPress={() => setEditing(false)}
                    isButtonDisabled={false}
                    text="Done editing"
                    heightOverride="30px"
                  />
                </span>
              </Tooltip>
            )}
            {/* Icon spacing as in the app's other toolbars (deal page, My Tasks). */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                "& .MuiIconButton-root": { p: 0.6 },
                "& .MuiIconButton-root .MuiSvgIcon-root": { fontSize: 22 },
              }}
            >
              {canEdit && !editing && (
                <CustomIconButton
                  tooltip="Edit pipeline"
                  icon={
                    <Box
                      component="img"
                      src={pencilIcon}
                      alt="Edit pipeline"
                      sx={{ width: 20, height: 20, display: "block" }}
                    />
                  }
                  onClick={() => setEditing(true)}
                />
              )}
              <DataCompletenessButton />
              <NotificationBellButton />
              {pipelineMenuItems.length > 0 && (
                <MenuDropDown
                  title="Pipeline Menu"
                  tooltip="Menu"
                  menuItems={pipelineMenuItems}
                />
              )}
            </Box>
          </Box>
        </SimpleToolbar>
      </Box>
      <Box sx={{ px: "20px", py: 1.25 }}>
        <PipelineFilterBar {...filterProps} />
      </Box>
    </>
  );
}

/** Whether other people's changes are streaming in, or our saves are in flight. */
function LiveStatus({
  isLive,
  isSaving,
}: {
  isLive: boolean;
  isSaving: boolean;
}) {
  return (
    <Tooltip
      title={
        isLive
          ? "Changes by others appear here automatically"
          : "Reconnecting: the pipeline will refresh when the connection returns"
      }
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          fontSize: 12,
          fontFamily: '"Helvetica Neue", sans-serif',
          color: "#667085",
          whiteSpace: "nowrap",
        }}
      >
        {isSaving ? (
          <CircularProgress size={10} thickness={6} sx={{ color: "#1B7A63" }} />
        ) : (
          <Box
            component="span"
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: isLive ? "#1B7A63" : "#C4C4C4",
            }}
          />
        )}
        {isSaving ? "Saving…" : isLive ? "Live" : "Offline"}
      </Box>
    </Tooltip>
  );
}
